/**
 * GBIX Engine Configuration
 * 
 * GBIX items are special tags that can be displayed as images on the map.
 * If a user's profile has one or more of these items as tags, one is randomly
 * selected and displayed as a half-transparent image in the right bottom of the map.
 */

export const GBIX_ITEMS = [
  {
    id: 'cats',
    name: 'Cats',
    imagePath: '/gbix/cat.png', // Image with transparent background
    tags: ['cats', 'cat', 'kitten', 'kittens', 'feline', 'felines']
  },
  {
    id: 'dogs',
    name: 'Dogs',
    imagePath: '/gbix/dog.png',
    tags: ['dogs', 'dog', 'puppy', 'puppies', 'canine', 'canines']
  },
  {
    id: 'hiking',
    name: 'Hiking',
    imagePath: '/gbix/hiking.png',
    tags: ['hiking', 'hike', 'trail', 'trails', 'mountain', 'mountains', 'outdoor', 'outdoors']
  },
  {
    id: 'coffee',
    name: 'Coffee',
    imagePath: '/gbix/coffee.png',
    tags: ['coffee', 'cafe', 'espresso', 'latte', 'cappuccino', 'barista']
  },
  {
    id: 'music',
    name: 'Music',
    imagePath: '/gbix/music.png',
    tags: ['music', 'musician', 'guitar', 'piano', 'dj', 'song', 'songs', 'album', 'albums']
  },
  {
    id: 'photography',
    name: 'Photography',
    imagePath: '/gbix/photography.png',
    tags: ['photography', 'photo', 'photos', 'camera', 'photographer', 'photoshoot']
  },
  {
    id: 'travel',
    name: 'Travel',
    imagePath: '/gbix/travel.png',
    tags: ['travel', 'traveling', 'travelling', 'trip', 'trips', 'vacation', 'adventure', 'explore']
  },
  {
    id: 'reading',
    name: 'Reading',
    imagePath: '/gbix/reading.png',
    tags: ['reading', 'books', 'book', 'literature', 'novel', 'novels', 'library']
  },
  {
    id: 'cooking',
    name: 'Cooking',
    imagePath: '/gbix/cooking.png',
    tags: ['cooking', 'cook', 'chef', 'recipe', 'recipes', 'baking', 'bake', 'kitchen']
  },
  {
    id: 'fitness',
    name: 'Fitness',
    imagePath: '/gbix/fitness.png',
    tags: ['fitness', 'gym', 'workout', 'exercise', 'training', 'yoga', 'running', 'runner']
  }
];

/**
 * Get matching GBIX items based on user tags
 * @param {Array<string>} userTags - Array of user's tags
 * @returns {Array<Object>} Array of matching GBIX items
 */
export const getMatchingGbixItems = (userTags) => {
  if (!userTags || !Array.isArray(userTags) || userTags.length === 0) {
    return [];
  }

  // Normalize user tags to lowercase for comparison
  const normalizedUserTags = userTags.map(tag => tag.toLowerCase().trim());
  
  // Find matching GBIX items
  const matchingItems = GBIX_ITEMS.filter(gbixItem => {
    return gbixItem.tags.some(gbixTag => 
      normalizedUserTags.some(userTag => 
        userTag === gbixTag || userTag.includes(gbixTag) || gbixTag.includes(userTag)
      )
    );
  });

  return matchingItems;
};

/**
 * Randomly select one GBIX item from matching items
 * @param {Array<string>} userTags - Array of user's tags
 * @returns {Object|null} Selected GBIX item or null if no matches
 */
export const selectRandomGbixItem = (userTags) => {
  const matchingItems = getMatchingGbixItems(userTags);
  
  if (matchingItems.length === 0) {
    return null;
  }

  // Randomly select one item
  const randomIndex = Math.floor(Math.random() * matchingItems.length);
  return matchingItems[randomIndex];
};

